import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
  DialogPortal,
  DialogClose,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { ChevronDown, X } from "lucide-react";
import { useState, useEffect } from "react";
import { getTradingAssets } from "../services/digitalWalletService";
import { getAssetChains } from "../services/tradingDataService";
import { Skeleton } from "./ui/skeleton";
import coin from "../assets/images/placeholder/crypto-colour.png";
import AssetList from "./AssetList";

const AssetNetSelector = ({
  setSelectedAsset,
  setSelectedNetwork,
  selectedAsset,
  selectedNetwork,
  action,
  FilledCheck,
}) => {
  const [open, setOpen] = useState(false);
  const [defaultAssets, setdefaultAssets] = useState([]);
  const [network, setNetwork] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const data = await getTradingAssets("Crypto");
        setdefaultAssets(data.data.data);
      } catch (err) {
        console.error("Error fetching assets:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAssets();
  }, []);

  // Fetch chains per asset like CurrencySelector does
  useEffect(() => {
    if (!defaultAssets || defaultAssets.length === 0) return;

    const fetchChains = async () => {
      try {
        const results = await Promise.all(
          defaultAssets.map(async (asset) => {
            const chainData = await getAssetChains(asset.assetId);
            return chainData || [];
          })
        );
        const combinedNetworks = results.flat();
        setNetwork(combinedNetworks);
      } catch (error) {
        console.error("Error loading networks:", error);
      }
    };

    fetchChains();
  }, [defaultAssets]);

  useEffect(() => {
    if (
      action === "update" &&
      defaultAssets &&
      defaultAssets.length > 0 &&
      selectedAsset &&
      network.length > 0
    ) {
      const selectedAddress = defaultAssets.find(
        (cryp) => cryp.assetId === selectedAsset || cryp._id === selectedAsset
      );
      if (selectedAddress) {
        setSelectedAsset(selectedAddress);
      }
    }
  }, [action, selectedAsset, defaultAssets, network, setSelectedAsset]);

  useEffect(() => {
    if (action === "update" && network.length > 0 && selectedNetwork) {
      const selectedNetworkFind = network.find(
        (net) => net._id === selectedNetwork || net.id === selectedNetwork
      );
      if (selectedNetworkFind) {
        setSelectedNetwork(selectedNetworkFind);
      }
    }
  }, [action, selectedNetwork, network, setSelectedNetwork]);

  const handleSelect = (asset) => {
    setSelectedAsset(asset);
    FilledCheck();
  };

  const handleSelectNet = (net) => {
    setSelectedNetwork(net);
    FilledCheck();
    setOpen(false);
  };

  const filteredAssets = defaultAssets?.filter(
    (assets) =>
      assets?.asset?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      assets?.assetName?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={loading}
        className="w-full h-[88px] flex items-center justify-between px-6 rounded-2xl border border-[#CECBD7] bg-white hover:ring-2 hover:ring-[#57249C] transition-all cursor-pointer text-left disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <div className="flex items-center gap-2 flex-1">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-500"></div>
            <span className="text-[14px] text-gray-500">Loading...</span>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img
                src={
                  selectedAsset && selectedAsset.assetIcon
                    ? selectedAsset.assetIcon
                    : coin
                }
                alt={selectedAsset ? selectedAsset.assetName : "Default Asset"}
                width={40}
                height={40}
                className="flex-shrink-0 rounded-full"
              />
              <div className="text-left min-w-0 flex-1">
                <div className="text-[16px] font-semibold text-[#423E51] truncate">
                  {selectedAsset?.asset
                    ? selectedAsset.asset.toUpperCase()
                    : "Select..."}
                </div>
                {selectedNetwork?.chain && (
                  <div className="text-[12px] text-[#6D6584] font-normal truncate">
                    {selectedNetwork.chain}
                  </div>
                )}
              </div>
            </div>
            <ChevronDown
              className="text-gray-400 flex-shrink-0 ml-2"
              size={20}
            />
          </>
        )}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogPortal>
          <DialogOverlay className="bg-brand-primary/45 backdrop-blur-[4px] fixed inset-0" />
          <DialogContent
            className="max-w-[95vw] md:max-w-md rounded-lg md:mx-auto"
            aria-describedby="saved-addresses-description"
          >
            <DialogClose asChild>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="w-8 h-8 md:w-10 md:h-10 absolute top-2 md:top-4 right-2 md:right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <X size={16} className="md:size-[18px] text-gray-700" />
              </Button>
            </DialogClose>
            <DialogHeader className="px-4 md:px-6">
              <DialogTitle className="text-base md:text-lg font-semibold">
                Choose Asset
              </DialogTitle>
            </DialogHeader>
            <div id="saved-addresses-description" className="mt-4 px-4 md:px-6">
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Search assets..."
                  className="w-full border rounded-lg px-3 py-2 text-sm md:text-base"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="overflow-y-auto max-h-[300px] px-2">
                {loading ? (
                  <>
                    <Skeleton className="w-full flex items-center justify-between py-8 rounded-2xl bg-gray-200 mb-2" />
                    <Skeleton className="w-full flex items-center justify-between py-8 rounded-2xl bg-gray-200 mb-2" />
                    <Skeleton className="w-full flex items-center justify-between py-8 rounded-2xl bg-gray-200 mb-2" />
                  </>
                ) : filteredAssets?.length > 0 ? (
                  <AssetList
                    ownedAssets={filteredAssets}
                    network={network}
                    handleSelect={handleSelect}
                    handleSelectNet={handleSelectNet}
                  />
                ) : (
                  <p className="text-center text-gray-500 py-4">
                    No assets found
                  </p>
                )}
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </>
  );
};

export default AssetNetSelector;
